import { portalMode } from './portal'

/**
 * The campaign a visit came from: the campaign tags (utm_*) and ad click IDs (gclid, gbraid,
 * wbraid from Google Ads, fbclid from Meta) of the address the app was opened with. arvaseo.com
 * passes them along on its "Start free" links (site/main.js), so a sign-up can still be credited
 * to its campaign when the visitor makes their cookie choice here rather than on the website.
 *
 * Read once when the app loads and kept in memory for this tab: nothing is stored. The tags go
 * with a sign-up (the account keeps them, and which ad platform the click came from, never a
 * click ID). The click IDs reach Google's tag and Meta's cookie only with consent to ad
 * measurement (lib/analytics). Never on an agency's portal address.
 */
export const CAMPAIGN_TAGS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
export const GOOGLE_CLICK_IDS = ['gclid', 'gbraid', 'wbraid'] as const

type Tag = (typeof CAMPAIGN_TAGS)[number]
type GoogleClickId = (typeof GOOGLE_CLICK_IDS)[number]

export interface Campaign {
  tags: Partial<Record<Tag, string>>
  google: Partial<Record<GoogleClickId, string>>
  fbclid: string | null
}

/** What a sign-up sends: the campaign tags, and which ad platform the click came from. */
export interface SignUpSource {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  click?: 'google' | 'meta'
}

const CLICK_ID = /^[\w-]{1,500}$/

export function readCampaign(search: string): Campaign {
  const params = new URLSearchParams(search)
  const campaign: Campaign = { tags: {}, google: {}, fbclid: null }
  for (const tag of CAMPAIGN_TAGS) {
    const value = params.get(tag)?.trim()
    if (value) campaign.tags[tag] = value.slice(0, 150)
  }
  for (const id of GOOGLE_CLICK_IDS) {
    const value = params.get(id)
    if (value && CLICK_ID.test(value)) campaign.google[id] = value
  }
  const fbclid = params.get('fbclid')
  campaign.fbclid = fbclid && CLICK_ID.test(fbclid) ? fbclid : null
  return campaign
}

let landing: Campaign = portalMode
  ? readCampaign('')
  : readCampaign(typeof window === 'undefined' ? '' : window.location.search)

/** The campaign of the address this tab opened with. */
export function landingCampaign(): Campaign {
  return landing
}

/** Reads the campaign again (tests, or an address the app was opened with later). */
export function captureCampaign(search: string = window.location.search) {
  landing = portalMode ? readCampaign('') : readCampaign(search)
}

/** The `signup_source` a registration request carries, when the visit came through a campaign. */
export function signUpSource(campaign: Campaign = landing): { signup_source?: SignUpSource } {
  const click: SignUpSource['click'] =
    Object.keys(campaign.google).length > 0 ? 'google' : campaign.fbclid ? 'meta' : undefined
  const source: SignUpSource = { ...campaign.tags, ...(click ? { click } : {}) }
  return Object.keys(source).length > 0 ? { signup_source: source } : {}
}

/**
 * The query string Google's tag sees on the first page of the visit: the campaign tags and
 * Google's click IDs (so Analytics credits the campaign and Google Ads keeps its click), and
 * nothing else from the address, which can hold reset and invitation tokens.
 */
export function googleCampaignQuery(campaign: Campaign = landing): string {
  const params = new URLSearchParams()
  for (const tag of CAMPAIGN_TAGS) {
    const value = campaign.tags[tag]
    if (value) params.set(tag, value)
  }
  for (const id of GOOGLE_CLICK_IDS) {
    const value = campaign.google[id]
    if (value) params.set(id, value)
  }
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}
